import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Moon, Sun, Loader2 } from 'lucide-react';
import InstallAppCard from './InstallAppCard';
import { usePushNotifications } from '../../hooks/usePushNotifications';

const CATEGORIES = [
  { id: 'messages', label: 'Nuovi messaggi', description: 'WhatsApp, Instagram, Facebook e richieste dal sito' },
  { id: 'appointments', label: 'Appuntamenti', description: 'Nuove prenotazioni, promemoria e disdette' },
  { id: 'phone', label: 'Richieste dal telefono', description: 'Prenotazioni raccolte dall\'assistente telefonico' },
  { id: 'reviews', label: 'Recensioni', description: 'Nuove recensioni Google, le critiche per prime' },
  { id: 'social', label: 'Social', description: 'Post da approvare, pubblicazioni e report' },
  { id: 'payments', label: 'Pagamenti', description: 'Gift card, acconti e abbonamento' },
  { id: 'inventory', label: 'Magazzino', description: 'Prodotti in esaurimento' },
];

const PreferencesTab = () => {
  const { theme, toggleTheme } = useTheme();
  const { API, getAuthHeaders } = useAuth();
  const push = usePushNotifications();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/preferences`, getAuthHeaders());
      setPrefs(res.data);
    } catch {
      setPrefs({ push_enabled: true, categories: {} });
    }
  }, [API, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setPrefs(next);
    setSaving(true);
    try {
      await axios.put(`${API}/notifications/preferences`, next, getAuthHeaders());
    } catch {
      toast.error('Preferenze non salvate');
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (id, value) =>
    save({ ...prefs, categories: { ...prefs.categories, [id]: value } });

  return (
    <div className="space-y-6">
      <InstallAppCard />

      <Card>
        <CardHeader>
          <CardTitle className="font-outfit">Aspetto</CardTitle>
          <CardDescription>Personalizza l'aspetto dell'app</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Modalità Scura</Label>
              <p className="text-sm text-muted-foreground">Passa dal tema chiaro a quello scuro</p>
            </div>
            <div className="flex items-center gap-2">
              <Sun className="w-4 h-4 text-muted-foreground" />
              <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="theme-switch" />
              <Moon className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="notification-prefs-card">
        <CardHeader>
          <CardTitle className="font-outfit flex items-center gap-2">
            Avvisi sul telefono
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          </CardTitle>
          <CardDescription>
            Scegli cosa ti arriva come notifica. Nella campanella dentro l'app trovi sempre tutto.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!prefs ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-4 pb-4 border-b">
                <div className="space-y-0.5 min-w-0">
                  <Label className="text-base">Notifiche su questo dispositivo</Label>
                  <p className="text-sm text-muted-foreground">
                    {!push.supported
                      ? 'Non supportate da questo browser'
                      : push.devices > 0
                        ? `${push.devices} dispositiv${push.devices === 1 ? 'o collegato' : 'i collegati'}`
                        : 'Nessun dispositivo collegato'}
                  </p>
                </div>
                <Switch checked={push.enabled} disabled={!push.supported || push.busy}
                  onCheckedChange={push.toggle} data-testid="pref-push-enabled" />
              </div>

              {push.enabled && (
                <div className="flex flex-col sm:flex-row gap-2 pb-2">
                  <Button variant="outline" size="sm" className="min-h-[40px]"
                    onClick={push.sendTest} disabled={push.busy} data-testid="pref-test-push">
                    Invia notifica di test
                  </Button>
                  {push.devices > 1 && (
                    <Button variant="ghost" size="sm" className="min-h-[40px]"
                      onClick={push.removeOtherDevices} disabled={push.busy} data-testid="pref-remove-devices">
                      Scollega gli altri dispositivi
                    </Button>
                  )}
                </div>
              )}
              {CATEGORIES.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5 min-w-0">
                    <Label className="text-base">{c.label}</Label>
                    <p className="text-sm text-muted-foreground">{c.description}</p>
                  </div>
                  <Switch
                    checked={prefs.categories?.[c.id] !== false}
                    disabled={!push.enabled}
                    onCheckedChange={(v) => toggleCategory(c.id, v)}
                    data-testid={`pref-${c.id}`}
                  />
                </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-outfit text-destructive">Zona Pericolosa</CardTitle>
          <CardDescription>Azioni irreversibili sull'account</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="destructive" data-testid="delete-account-btn">Elimina Account</Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PreferencesTab;
